import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload } from "lucide-react";
import { inputCls, labelCls, btn } from "@/components/admin/ui";

const SIZES = ["small", "medium", "large", "full"];

const emptyProject = {
  title: "", descriptor: "", award: "", category: "spec", model: "", format: "",
  prompt: "", youtube_id: "", custom_thumbnail: "", custom_video: "", size: "small",
};

const extractYouTubeId = (val) => {
  if (!val) return val;
  const m = val.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : val.trim();
};

const UploadField = ({ label, field, accept, value, uploading, onUpload }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <label className={`${btn} border border-hairline text-mute hover:text-txt cursor-pointer inline-flex items-center gap-2`}>
      <Upload size={13} /> {uploading === field ? "…" : "Upload"}
      <input type="file" accept={accept} hidden onChange={(e) => onUpload(e.target.files[0], field)} />
    </label>
    {value && <p className="font-mono text-[10px] text-lav mt-1 truncate">{field === "custom_video" ? "video" : "image"} set ✓</p>}
  </div>
);

export const ProjectModal = ({ project, categories, onClose, onSaved }) => {
  const [form, setForm] = useState(project || emptyProject);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const upload = async (file, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, youtube_id: extractYouTubeId(form.youtube_id) };
      if (form.id) await api.put(`/projects/${form.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[88vh] overflow-y-auto bg-surface border border-hairline rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="project-modal">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-xl">{form.id ? "Edit project" : "New project"}</h3>
          <button onClick={onClose} className="text-mute hover:text-txt"><X size={18} /></button>
        </div>

        <label className={labelCls}>Title</label>
        <input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="pf-title" />

        <label className={labelCls}>Client / descriptor</label>
        <input className={inputCls} value={form.descriptor} onChange={(e) => set("descriptor", e.target.value)} />

        <label className={labelCls}>Award line (award films)</label>
        <input className={inputCls} value={form.award || ""} onChange={(e) => set("award", e.target.value)} placeholder="🏆 1st place · …" data-testid="pf-award" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Category</label>
            <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Card size</label>
            <select className={inputCls} value={form.size} onChange={(e) => set("size", e.target.value)}>
              {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <label className={labelCls}>Model / tech tags</label>
        <input className={inputCls} value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="seedance 2.0" />

        <label className={labelCls}>Format tag</label>
        <input className={inputCls} value={form.format} onChange={(e) => set("format", e.target.value)} placeholder="16:9 · spec ad (use 9:16 for vertical/reels)" />

        <label className={labelCls}>YouTube link or ID</label>
        <input className={inputCls} value={form.youtube_id} onChange={(e) => set("youtube_id", e.target.value)} placeholder="https://youtu.be/… or VIDEO_ID" />

        <label className={labelCls}>Prompt / brief text</label>
        <textarea className={`${inputCls} min-h-[90px]`} value={form.prompt} onChange={(e) => set("prompt", e.target.value)} />

        <div className="grid grid-cols-2 gap-3 mt-2">
          <UploadField label="Custom thumbnail" field="custom_thumbnail" accept="image/*" value={form.custom_thumbnail} uploading={uploading} onUpload={upload} />
          <UploadField label="Custom video" field="custom_video" accept="video/*" value={form.custom_video} uploading={uploading} onUpload={upload} />
        </div>

        <div className="flex gap-3 mt-7">
          <button onClick={save} disabled={saving} className={`${btn} bg-lav text-ink flex-1`} data-testid="pf-save">
            {saving ? "saving…" : "Save"}
          </button>
          <button onClick={onClose} className={`${btn} border border-hairline text-mute`}>Cancel</button>
        </div>
      </div>
    </div>
  );
};
